import React, { useContext, useEffect, useMemo, useState } from "react";

import { Avatar, Box, Button, Card, CardActions, CardContent, CardHeader, Grid, InputLabel, MenuItem, TextField, Typography } from "@mui/material";
import CircleIcon from '@mui/icons-material/Circle';

import NumberFormat from 'react-number-format';
import { Utils } from 'common/Utils';
import { PortalNames } from "common/Portals";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";

import LoaderAnimation from "pages/LoaderAnimation";
import SortBox from "components/Filters/SortBox";
import ColorFilterBox from "components/Filters/ColorFilterBox";

import { apiGet } from "app/Services/ApiService";
import { ap3ApiUrls } from "common/ApiUrls";

export default function BrokerDashboard() {
    const [isLoading, setIsLoading] = useState(false);
    const [searchFilter, setSearchFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [sortValue, setSortValue] = useState("name");
    const [colorFilter, setColorFilter] = useState("none");

    const [companies, setCompanies] = useState([]);

    const colors = useMemo(() => {
        var groups = [];
        
        if (companies && companies.length > 0) {
            var c = companies.sort((a,b) => { return a.status.value - b.status.value });

            groups = Utils.groupByArrays(c, (c) => c.status.color.displayName)
                .map(g => g.key);
        }

        return groups;
    },[companies]);

    const statuses = useMemo(() => {
        var groups = [];
        
        if (companies && companies.length > 0) {
            groups = Utils.groupByArrays(companies, (c) => c.status.description)
                .map(g => g.key);
        }

        return groups;
    },[companies])

    const displayedCompanies = useMemo(() => {
        var comp = companies;

        if (searchFilter) {
            var regEx = new RegExp(Utils.escapeRegExp(searchFilter), 'i');

            comp = comp.filter(({ name }) => name.match(regEx));
        }

        if (statusFilter) {
            comp = comp.filter(({ status }) => status.description === statusFilter);
        }

        if (colorFilter && colorFilter !== "none") {
            comp = comp.filter(({ status }) => status.color.displayName === colorFilter);
        }

        if (sortValue == "name") {
            comp = comp.sort((a,b) => a.name.localeCompare(b.name))
        }
        else {
            comp = comp.sort((a,b) => a.status.value - b.status.value)
        }

        return comp;
    },[companies, searchFilter, statusFilter, sortValue, colorFilter])

    useEffect(() => {
        getCompanies();
    }, []);

    const getCompanies = () => {
        setIsLoading(true);

        apiGet(ap3ApiUrls.getDashboard)
            .then((response) => {
                console.log('dashboard companies',response.data);
                setCompanies(response.data.filter(c => (c.companyType!='AP3' && c.companyType!='Broker' )));
            })
            .catch(e => {
                //console.log(e);
                //setHasInitializationError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    return (
        <Box>
            <SearchAndFilterBox
                searchFilter={searchFilter}
                setSearchFilter={setSearchFilter}
                sortValue={sortValue}
                setSortValue={setSortValue}
                colors={colors}
                colorFilter={colorFilter}
                setColorFilter={setColorFilter}
                statuses={statuses}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
            />
            {isLoading && <LoaderAnimation />}
            {!isLoading && companies !== undefined ?
                <Grid container spacing={2}>
                    {displayedCompanies.map((c,idx) => {
                        return (
                            <Grid key={idx} item>
                                <CompanySummary company={c}/>
                            </Grid>
                            )
                    })}
                </Grid>
                : "" }
        </Box>
      );
}

function SearchAndFilterBox({ searchFilter, setSearchFilter, sortValue, setSortValue, colors, colorFilter, setColorFilter, statuses, statusFilter, setStatusFilter }) {
    return (
        <Box
            margin={1}
            component="form"
            noValidate
            autoComplete="off"
        >
            <Grid container spacing={2}>
                <Grid item xs={4}><BasicSearch searchFilter={searchFilter} setSearchFilter={setSearchFilter} /></Grid>
                <Grid item xs={3}><SortBox sortValue={sortValue} setSortValue={setSortValue}/></Grid>
                <Grid item xs={3}><ColorFilterBox colors={colors} colorFilter={colorFilter} setColorFilter={setColorFilter}/></Grid>
                {/* <Grid item xs={3}><RegionFilterBox /></Grid> */}
                <Grid item xs={3}><StatusFilterBox statuses={statuses} statusFilter={statusFilter} setStatusFilter={setStatusFilter} /></Grid>
            </Grid>
        </Box>
    );
}

function BasicSearch({ searchFilter, setSearchFilter }) {
    return (
      <>
        <InputLabel>&nbsp;</InputLabel> {/* hack alignment */}
        <TextField
            key="search"
            fullWidth
            variant="standard"
            label="Search"
            id="search"
            type="search"
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
        />
      </>
    )
}

const StatusFilterBox = ({ statuses, statusFilter, setStatusFilter }) =>{
    return (
        <TextField
            fullWidth
            variant="standard"
            select
            labelid="filter-status-label"
            id="filter-status"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            label="Status Filter"
        >
            <MenuItem value="">
                <em>All</em>
            </MenuItem>
            {statuses.map((value, index) => <MenuItem key={`status-menu-${index}`} value={value}>{value}</MenuItem>)}
        </TextField>
    )
}

function CompanySummary(props) {
    const { setCompany, setCurrentPortal } = useContext(PrivateRoutesContext);
    let companyInfo = props.company;

    const handleClick = (e) => {
      if(companyInfo.id){
        setCompany(companyInfo.id);
        setCurrentPortal(PortalNames.Employer.Name);
        window.location.href=`/${PortalNames.Employer.Path}/dashboard`;
      }
    }

    return (
      <Card
        sx={{
          maxWidth: 450,
          minHeight:"100%",
          border: 10,
          borderColor: companyInfo.status.color.htmlValue,
          padding: 1,
        }}>
        <CompanyHeader {...props} />
        <CardContent >
          <Typography gutterBottom variant="h5" component="div">
            <p>Status: {companyInfo.status.description} <CircleIcon sx={{color: companyInfo.status.color.htmlValue}} /></p>
            {companyInfo?.outstandingInvoices ? <InvoiceSummary
                                      dateTitle="Outstanding Invoices"
                                      date={companyInfo?.outstandingInvoices.oldestDate}
                                      amountTitle="Outstanding Balance"
                                      amount={companyInfo?.outstandingInvoices?.totalAmount} />
                                  : <></>}
            {companyInfo?.mostRecentClaims ? <InvoiceSummary
                                      dateTitle="Most Recent Claims"
                                      date={companyInfo?.mostRecentClaims.mostRecentDate}
                                      amountTitle="Most Recent Claims Amt"
                                      amount={companyInfo?.claims?.totalAmount} />
                                  : <></>}
            {companyInfo?.mostRecentPremiums ? <InvoiceSummary
                                      dateTitle="Most Recent Premiums"
                                      date={companyInfo?.mostRecentPremiums.mostRecentDate}
                                      amountTitle="Most Recent Premiums Amt"
                                      amount={companyInfo?.mostRecentPremiums?.totalAmount} />
                                  : <></>}
            {companyInfo?.mostRecentHoursUpload ? <p>Hours Received : {Utils.formatDate(companyInfo.mostRecentHoursUpload, false)} </p> : <></>}
            {companyInfo?.mostRecentComplianceDate ? <p>Compliance Processed : {Utils.formatDate(companyInfo.mostRecentComplianceDate, false)}</p> : <></>}
          </Typography>
          <Typography variant="body2" color="text.secondary" >
            <span dangerouslySetInnerHTML = {{__html: companyInfo.notes}} />
          </Typography>
        </CardContent>
        <CardActions>
          <Button onClick={handleClick} variant="outlined">View Employer Portal</Button>
        </CardActions>
      </Card>
    );
}

function CompanyHeader(props) {
    const [avatar, setAvatar] = useState("");

    useEffect(() => {
        let companyInfo = props.company;
        let a = null;
        if (companyInfo.image && companyInfo.image.data !== null) {
            a = <img alt={companyInfo.name}
                    src={`data:${companyInfo.image.mime};base64, ${companyInfo.image.data}`}
                    style={{height:"140px", maxWidth:"100%"}}
                />
        }
        else if (companyInfo.image && companyInfo.image.uri !== "") {
            a = <Avatar
                    sx={{ width: 100, height: 100 }}
                    {...stringAvatar(companyInfo.name)}
                    alt={companyInfo.name}
                    src={companyInfo.image.uri}
                />
        } else {
            a = <Avatar {...stringAvatar(companyInfo.name)} />
        }

        setAvatar(a);
    },[props])

    const stringToAvatarColor = (string, s = 30, l = 50) => {
        let hash = 0;
        let i;
        /* eslint-disable no-bitwise */
        for (i = 0; i < string.length; i += 1) {
          hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        var h = hash % 360;
        return `hsl(${h}), ${s}%, ${l}%`;
        /* eslint-enable no-bitwise */
      }
  
      const stringAvatar = (name) => {
          let nameUpperCase = name.toUpperCase();
          let n = nameUpperCase.trim().split(' ').filter(String); //remove empty spaces, caused when multiple spaces used
          let a = nameUpperCase.slice(0,2)
          // console.log(n,"before if");
          if (n.length>1)
          {
              a = `${n[0][0]}${n[1][0]}`
          }
          
          var bgcolor = stringToAvatarColor(name.trim());
  
          return {
              sx: {
                  width:100,
                  height:100,
                  bgcolor: bgcolor,
              },
              children: `${a}`,
          };
      }

    return (
        <Box sx={{height: 200, textAlign:"center"}}>
            <CardHeader
                title={props.company.name}
                titleTypographyProps={{variant:"h4", textAlign:"center"}}
                avatar={avatar}
            />
        </Box>
    )
}

function InvoiceSummary({dateTitle, date, amountTitle, amount}) {
    return (
        <>
            <p>{dateTitle}: {Utils.formatDate(date,false)} </p>
            <p>{amountTitle}: <NumberFormat value={amount} displayType={'text'} thousandSeparator={true} prefix={'$'} /></p>
        </>
    )
}